import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const BENTO = [
  {
    icon: "account_tree",
    color: "text-primary",
    accent: "bg-gradient-accent",
    title: "Misconfigurations",
    desc: "Automatically detect open ports, exposed instances, and architectural flaws before they are exploited.",
  },
  {
    icon: "key",
    color: "text-secondary",
    accent: "bg-secondary",
    title: "Insecure IAM",
    desc: "Identify overly permissive policies, stale accounts, and privilege escalation vectors in real-time.",
  },
  {
    icon: "database",
    color: "text-primary",
    accent: "bg-primary",
    title: "Exposed Storage",
    desc: "Monitor S3 buckets, blob storage, and databases for public access and encryption weaknesses.",
  },
  {
    icon: "build",
    color: "text-on-surface dark:text-surface-bright",
    accent: "bg-gradient-accent",
    title: "Remediation Guidance",
    desc: "Get prioritized, step-by-step instructions and code snippets to fix issues fast.",
    span: true,
  },
];

const FRAMEWORKS = {
  gdpr: {
    label: "GDPR",
    title: "GDPR Readiness Assessment",
    controls: [
      { id: "Art. 32", desc: "Data Storage Encryption Verified", status: "pass" },
      { id: "Art. 25", desc: "Data Minimization Configurations", status: "pass" },
      { id: "Art. 33", desc: "Breach Notification Telemetry", status: "warning", note: "1 asset lacks logging" },
      { id: "Art. 5", desc: "Cross-border Transfer Mapping", status: "pass" },
    ],
  },
  soc2: {
    label: "SOC 2 Type II",
    title: "SOC 2 Type II Controls",
    controls: [
      { id: "CC6.1", desc: "Logical Access Controls Active", status: "pass" },
      { id: "CC6.6", desc: "System Boundary Protection", status: "pass" },
      { id: "CC7.1", desc: "Configuration Monitoring", status: "fail", note: "2 S3 buckets publicly readable" },
      { id: "CC8.1", desc: "Change Management Tracking", status: "pass" },
    ],
  },
  iso: {
    label: "ISO 27001",
    title: "ISO 27001:2022 Annex A",
    controls: [
      { id: "A.8.1", desc: "Asset Inventory Completeness", status: "pass" },
      { id: "A.9.2", desc: "User Access Provisioning", status: "pass" },
      { id: "A.12.6", desc: "Technical Vulnerability Management", status: "warning", note: "3 critical CVEs unpatched" },
      { id: "A.13.1", desc: "Network Security Management", status: "pass" },
    ],
  },
  pci: {
    label: "PCI DSS",
    title: "PCI DSS v4.0",
    controls: [
      { id: "Req 1", desc: "Network Security Controls", status: "pass" },
      { id: "Req 3", desc: "Stored Account Data Protection", status: "pass" },
      { id: "Req 8", desc: "Identify & Authenticate Access", status: "pass" },
      { id: "Req 11", desc: "Regular Testing of Systems", status: "pass" },
    ],
  },
};

const STATUS_STYLE = {
  pass: { icon: "check_circle", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  warning: { icon: "warning", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  fail: { icon: "error", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
};

export default function ComplianceSection() {
  const [active, setActive] = useState("gdpr");
  const framework = FRAMEWORKS[active];

  return (
    <>
      <section className="mb-section-gap">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-headline-section text-headline-section text-on-surface dark:text-surface-bright font-semibold mb-stack-sm">
            Continuous Cloud Posture Assessment
          </h2>
          <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant">
            Automated detection across your entire cloud footprint.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENTO.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`glass-card rounded-2xl p-card-padding relative overflow-hidden group hover:-translate-y-1 transition-all duration-300 ${
                card.span ? "lg:col-span-1 md:col-span-2" : ""
              }`}
            >
              <div className={`absolute top-0 left-0 w-full h-1 ${card.accent} opacity-50 group-hover:opacity-100 transition-opacity`} />
              <div className={`w-12 h-12 rounded-full bg-surface-container dark:bg-white/10 flex items-center justify-center mb-stack-lg ${card.color} group-hover:scale-110 transition-transform`}>
                <span className="material-symbols-outlined">{card.icon}</span>
              </div>
              <h3 className="font-headline-card text-headline-card text-on-surface dark:text-surface-bright mb-stack-sm">
                {card.title}
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant text-sm">
                {card.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mb-section-gap" id="frameworks">
        <div className="glass-card rounded-[2rem] p-card-padding lg:p-16 border-t-4 border-t-primary relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
          <div className="grid lg:grid-cols-12 gap-12 relative z-10">
            <div className="lg:col-span-4 flex flex-col">
              <span className="font-eyebrow text-eyebrow text-secondary uppercase tracking-widest mb-stack-sm">
                Compliance Mapping
              </span>
              <h2 className="font-headline-section text-headline-section text-on-surface dark:text-surface-bright font-semibold mb-8">
                Auditable Proof
              </h2>
              <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant mb-8">
                Select a framework to view real-time control assessments and evidence gathering.
              </p>
              <div className="flex flex-col gap-3">
                {Object.entries(FRAMEWORKS).map(([key, fw]) => (
                  <button
                    key={key}
                    onClick={() => setActive(key)}
                    className={`w-full text-left px-6 py-4 rounded-xl border font-label-sm transition-all duration-200 flex justify-between items-center group ${
                      active === key
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-glass text-on-surface-variant dark:text-surface-variant hover:bg-surface-variant/30"
                    }`}
                  >
                    {fw.label}
                    <span
                      className={`material-symbols-outlined transition-all ${
                        active === key ? "opacity-100 translate-x-1" : "opacity-0 group-hover:opacity-50 group-hover:translate-x-1"
                      }`}
                    >
                      arrow_forward
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-8 bg-surface-dark rounded-2xl p-card-padding border border-surface-dark/50 shadow-2xl relative overflow-hidden">
              <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-secondary">policy</span>
                  <h3 className="font-data-mono text-data-mono text-white text-lg">{framework.title}</h3>
                </div>
                <div className="flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full border border-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-data-mono text-[10px] uppercase tracking-wider">Live Monitoring</span>
                </div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial="hidden"
                  animate="visible"
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
                  className="space-y-4"
                >
                  {framework.controls.map((c) => {
                    const s = STATUS_STYLE[c.status];
                    return (
                      <motion.div
                        key={c.id}
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                        className="flex items-center justify-between p-4 rounded-lg border border-white/5 bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <span className={`material-symbols-outlined ${s.color}`}>{s.icon}</span>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-data-mono text-data-mono text-white/50 bg-white/10 px-1.5 py-0.5 rounded">
                                {c.id}
                              </span>
                              <span className="font-body-md text-white text-sm font-medium">{c.desc}</span>
                            </div>
                            {c.note && <p className={`font-data-mono text-[11px] mt-1 ${s.color}`}>{c.note}</p>}
                          </div>
                        </div>
                        <div className="hidden sm:block">
                          <span className={`font-data-mono text-[10px] uppercase tracking-widest ${s.color} ${s.bg} px-2 py-1 rounded border`}>
                            Automated Evidence
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
